import React, { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AimieRobotAvatarIcon } from '../chat/ChatAvatarIcons';
import './LectureReminderNotice.css';

const NOTICE_GAP = 12;
const NOTICE_WIDTH = 304;
const VIEWPORT_MARGIN = 12;

function getNoticePosition(anchor) {
  const rect = anchor.getBoundingClientRect();
  const fitsToRight = window.innerWidth - rect.right >= NOTICE_WIDTH + NOTICE_GAP + VIEWPORT_MARGIN;

  if (fitsToRight) {
    return {
      placement: 'right',
      top: rect.top + (rect.height / 2),
      left: rect.right + NOTICE_GAP,
    };
  }

  return {
    placement: 'above',
    top: rect.top - NOTICE_GAP,
    left: Math.min(
      Math.max(rect.left, VIEWPORT_MARGIN),
      window.innerWidth - NOTICE_WIDTH - VIEWPORT_MARGIN,
    ),
  };
}

function LectureReminderNotice({
  anchorRef,
  t,
  theme = 'dark',
  skillName,
  title,
  message,
  closeAriaLabel,
  onClose,
}) {
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return undefined;
    }

    const updatePosition = () => setPosition(getNoticePosition(anchor));
    updatePosition();

    const resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(anchor);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRef]);

  if (!position) {
    return null;
  }

  return createPortal(
    <aside
      className={`lecture-reminder-notice lecture-reminder-notice--${position.placement} lecture-reminder-notice--${theme === 'dark' ? 'dark' : 'light'}`}
      style={{ top: position.top, left: position.left }}
      role="status"
    >
      <button
        type="button"
        className="lecture-reminder-notice__close"
        onClick={onClose}
        aria-label={closeAriaLabel || t('specific_skill_lecture_reminder_close_aria')}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <div className="lecture-reminder-notice__message">
        <span className="lecture-reminder-notice__avatar" aria-hidden="true">
          <AimieRobotAvatarIcon />
        </span>
        <div>
          <strong>{title || t('specific_skill_lecture_reminder_title')}</strong>
          <p>{message || t('specific_skill_lecture_reminder_message', { skill: skillName })}</p>
        </div>
      </div>
      <div className="lecture-reminder-notice__settings-hint">
        Hide or restore these: <strong>☰ → Settings → Suggestions</strong>
      </div>
    </aside>,
    document.body,
  );
}

export default LectureReminderNotice;
